import { useMemo } from 'react'

import type {
  F0DialogPrimaryAction,
  F0DialogPrimaryActionItem,
  F0DialogSecondaryAction,
  F0FormRef,
} from '@factorialco/f0-react'
import { CheckCircle } from '@factorialco/f0-react/icons/app'
import type { PurchaseOrderDetailFieldsFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import type { EditMode } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/useEditPurchaseOrder'

import { getDropdownItems, getSuccessMessage, isValidStatus } from './utils'
import { useStatusTransition } from './useStatusTransition'

interface UseFooterActionsParams {
  purchaseOrder: PurchaseOrderDetailFieldsFragment
  editMode: EditMode
  cancelEditing: () => void
  /** Callback for the "Review and Create" button (starts editing in create mode). */
  onReviewAndCreate: () => void
  formRef: React.MutableRefObject<F0FormRef | null>
}

interface FooterActions {
  primaryAction?: F0DialogPrimaryAction | F0DialogPrimaryActionItem[]
  secondaryAction?: F0DialogSecondaryAction
}

export function useFooterActions({
  purchaseOrder,
  editMode,
  cancelEditing,
  onReviewAndCreate,
  formRef,
}: UseFooterActionsParams): FooterActions {
  const { updatingStatus, handleUpdateStatus } = useStatusTransition(purchaseOrder)
  const currentStatus = purchaseOrder.status
  const isEditing = editMode !== null

  return useMemo(() => {
    if (isEditing) {
      const saveLabel =
        editMode === 'create'
          ? i18n.t('procurement.purchase_orders.detail.create_purchase_order')
          : i18n.t('procurement.purchase_orders.detail.save')

      return {
        primaryAction: {
          label: saveLabel,
          onClick: () => void formRef.current?.submit(),
        },
        secondaryAction: {
          label: i18n.t('procurement.purchase_orders.detail.cancel'),
          onClick: cancelEditing,
        },
      }
    }

    if (currentStatus === 'processing') {
      return {}
    }

    if (currentStatus === 'draft') {
      return {
        primaryAction: {
          label: i18n.t('procurement.purchase_orders.actions.review'),
          onClick: onReviewAndCreate,
        },
      }
    }

    if (currentStatus === 'received') {
      return {
        primaryAction: {
          label: i18n.t('procurement.purchase_orders.actions.close'),
          icon: CheckCircle,
          onClick: () =>
            void handleUpdateStatus(
              'closed',
              i18n.t('procurement.purchase_orders.responses.closed.success')
            ),
          loading: updatingStatus,
        },
      }
    }

    if (currentStatus === 'closed') {
      return {
        primaryAction: {
          label: i18n.t('procurement.purchase_orders.actions.reopen'),
          onClick: () =>
            void handleUpdateStatus(
              'pending',
              i18n.t('procurement.purchase_orders.responses.reopened.success')
            ),
          loading: updatingStatus,
        },
      }
    }

    // pending, ordered, partial — split button as primaryAction array
    const dropdownItems = getDropdownItems(currentStatus)
    const primaryActionItems: F0DialogPrimaryActionItem[] = dropdownItems.map((item) => ({
      value: item.value,
      label: item.label,
      icon: item.icon,
      loading: updatingStatus,
      onClick: () => {
        if (isValidStatus(item.value)) {
          void handleUpdateStatus(item.value, getSuccessMessage(item.value))
        }
      },
    }))

    return {
      primaryAction: primaryActionItems,
    }
  }, [
    isEditing,
    editMode,
    currentStatus,
    updatingStatus,
    cancelEditing,
    onReviewAndCreate,
    formRef,
    handleUpdateStatus,
  ])
}
